import { type Signal, useComputed } from "@suse-o11y/signal";
import React from "react";

import type { TableColFilter } from "~/components/table/col-filter";
import type { TableColOrder } from "~/components/table/col-order";
import { TableHeadCell } from "~/components/table/head-cell";
import { TableHeadCellFilter } from "~/components/table/head-cell-filter";
import { TableHeadCellSorter } from "~/components/table/head-cell-sorter";
import { cx } from "~/tools/class-variance";
import type { ImmutableMap } from "~/tools/immutable-map";

interface HeadCellProps {
	className?: string;
	columnId?: string;
	orders: undefined | Signal<ImmutableMap<string, TableColOrder>>;
	filters: undefined | Signal<ImmutableMap<string, TableColFilter>>;
	children?: React.ReactNode;
}

export const HeadCell = React.memo<HeadCellProps>(({ className, columnId, orders, filters, children }) => {
	const order = useComputed((monitor) => orders?.read(monitor).get(columnId ?? ""), [columnId, orders]);
	const filter = useComputed((monitor) => filters?.read(monitor).get(columnId ?? ""), [columnId, filters]);

	return (
		<TableHeadCell className={cx("items-center whitespace-nowrap", className)}>
			<div className="flex-1">{children}</div>

			{orders != null && columnId != null && (
				<TableHeadCellSorter
					onOrderChange={(next) => orders.write((current) => current.set(columnId, next))}
					order={order}
				/>
			)}

			{filters != null && columnId != null && (
				<TableHeadCellFilter
					filter={filter}
					onFilterChange={(next) => filters.write((current) => current.set(columnId, next))}
				/>
			)}
		</TableHeadCell>
	);
});
